import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import * as recruitmentService from '../../services/recruitmentService';
import { Application, updateApplicationStage, rateApplication, rejectApplication, convertToStaff, RecruitmentNote, getNotes, createNote, deleteNote, ActivityLog, getActivityLog } from '../../services/recruitmentService';
import InterviewScheduler from '../../components/recruitment/InterviewScheduler';
import EmailComposer from '../../components/recruitment/EmailComposer';
import OfferGenerator from '../../components/recruitment/OfferGenerator';

const PIPELINE_STAGES = [
  { id: 'applied', label: 'Applied', icon: '📝' },
  { id: 'screening', label: 'Screening', icon: '🔍' },
  { id: 'phone_screen', label: 'Phone Screen', icon: '📞' },
  { id: 'interview', label: 'Interview', icon: '💼' },
  { id: 'final_round', label: 'Final Round', icon: '⭐' },
  { id: 'offer', label: 'Offer', icon: '✉️' },
  { id: 'hired', label: 'Hired', icon: '✅' },
];

const ApplicationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [application, setApplication] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'resume' | 'notes' | 'activity'>('overview');
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [converting, setConverting] = useState(false);
  const [showInterviewScheduler, setShowInterviewScheduler] = useState(false);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [showOfferGenerator, setShowOfferGenerator] = useState(false);
  const [notes, setNotes] = useState<RecruitmentNote[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [activities, setActivities] = useState<ActivityLog[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);

  useEffect(() => {
    loadApplication();
  }, [id]);

  useEffect(() => {
    if (activeTab === 'notes' && id) {
      loadNotes();
    }
    if (activeTab === 'activity' && id) {
      loadActivities();
    }
  }, [activeTab, id]);

  const loadApplication = async () => {
    try {
      setLoading(true);
      const data = await recruitmentService.getApplication(id!);
      setApplication(data);
    } catch (error: any) {
      console.error('Failed to load application:', error);
      toast.error('Failed to load application');
    } finally {
      setLoading(false);
    }
  };

  const handleStageChange = async (newStage: string) => {
    try {
      await updateApplicationStage(id!, newStage);
      toast.success(`Moved to ${PIPELINE_STAGES.find(s => s.id === newStage)?.label}`);
      loadApplication();
    } catch (error: any) {
      console.error('Failed to update stage:', error);
      toast.error('Failed to update stage');
    }
  };

  const handleRating = async (rating: number) => {
    try {
      await rateApplication(id!, rating);
      toast.success('Rating updated');
      loadApplication();
    } catch (error: any) {
      console.error('Failed to rate:', error);
      toast.error('Failed to update rating');
    }
  };

  const handleReject = async () => {
    if (!window.confirm('Are you sure you want to reject this candidate?')) return;
    
    try {
      await rejectApplication(id!);
      toast.success('Candidate rejected');
      navigate('/recruitment#pipeline');
    } catch (error: any) {
      console.error('Failed to reject:', error);
      toast.error('Failed to reject candidate');
    }
  };

  const handleConvertToStaff = async () => {
    if (!window.confirm('Convert this candidate to a staff member? This will create their employee record.')) return;
    
    try {
      setConverting(true);
      const staffId = await convertToStaff(id!);
      toast.success('Successfully converted to staff member!');
      // Navigate to staff management with the new staff ID
      navigate(`/legal-hr/staff-management`);
    } catch (error: any) {
      console.error('Failed to convert:', error);
      toast.error(error.message || 'Failed to convert to staff');
    } finally {
      setConverting(false);
    }
  };

  const loadNotes = async () => {
    try {
      setLoadingNotes(true);
      const data = await getNotes(id!);
      setNotes(data);
    } catch (error: any) {
      console.error('Failed to load notes:', error);
      toast.error('Failed to load notes');
    } finally {
      setLoadingNotes(false);
    }
  };

  const loadActivities = async () => {
    try {
      setLoadingActivities(true);
      const data = await getActivityLog(id!);
      setActivities(data);
    } catch (error: any) {
      console.error('Failed to load activities:', error);
      toast.error('Failed to load activities');
    } finally {
      setLoadingActivities(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    try {
      setSavingNote(true);
      await createNote({
        application_id: id!,
        note: newNote.trim(),
        is_private: false,
      });
      toast.success('Note added successfully');
      setNewNote('');
      loadNotes(); // Reload notes
    } catch (error: any) {
      console.error('Failed to add note:', error);
      toast.error('Failed to add note');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    try {
      await deleteNote(noteId);
      toast.success('Note deleted');
      loadNotes();
    } catch (error: any) {
      console.error('Failed to delete note:', error);
      toast.error('Failed to delete note');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Application not found</p>
          <button
            onClick={() => navigate('/recruitment#pipeline')}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Back to Pipeline
          </button>
        </div>
      </div>
    );
  }

  const candidate = application.candidate || {};
  const job = application.job || {};
  const candidateName = `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim() || 'Unknown Candidate';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/recruitment#pipeline')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{candidateName}</h1>
                <p className="text-sm text-gray-500">{job.title || 'Unknown Position'}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {application.current_stage === 'hired' ? (
                <button
                  onClick={handleConvertToStaff}
                  disabled={converting}
                  className="px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all font-medium flex items-center gap-2 shadow-lg"
                >
                  {converting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Converting...</span>
                    </>
                  ) : (
                    <>
                      <span>✅</span>
                      <span>Convert to Staff</span>
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={handleReject}
                  className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors font-medium"
                >
                  Reject
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tabs */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="border-b border-gray-200">
                <div className="flex gap-1 p-2">
                  {[
                    { id: 'overview', label: 'Overview', icon: '👤' },
                    { id: 'resume', label: 'Resume', icon: '📄' },
                    { id: 'notes', label: 'Notes', icon: '📝' },
                    { id: 'activity', label: 'Activity', icon: '📊' },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as any)}
                      className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                        activeTab === tab.id
                          ? 'bg-purple-100 text-purple-700'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span className="mr-2">{tab.icon}</span>
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-6">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    {/* Contact Information */}
                    <section>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {candidate.email && (
                          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <span className="text-2xl">✉️</span>
                            <div>
                              <p className="text-xs text-gray-500">Email</p>
                              <a href={`mailto:${candidate.email}`} className="text-purple-600 hover:underline font-medium">
                                {candidate.email}
                              </a>
                            </div>
                          </div>
                        )}
                        {candidate.phone && (
                          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <span className="text-2xl">📞</span>
                            <div>
                              <p className="text-xs text-gray-500">Phone</p>
                              <a href={`tel:${candidate.phone}`} className="text-purple-600 hover:underline font-medium">
                                {candidate.phone}
                              </a>
                            </div>
                          </div>
                        )}
                        {candidate.location && (
                          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <span className="text-2xl">📍</span>
                            <div>
                              <p className="text-xs text-gray-500">Location</p>
                              <p className="text-gray-900 font-medium">{candidate.location}</p>
                            </div>
                          </div>
                        )}
                        {candidate.linkedin_url && (
                          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <span className="text-2xl">💼</span>
                            <div>
                              <p className="text-xs text-gray-500">LinkedIn</p>
                              <a 
                                href={candidate.linkedin_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-purple-600 hover:underline font-medium"
                              >
                                View Profile
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                    </section>

                    {/* Professional Summary */}
                    {candidate.summary && (
                      <section>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Professional Summary</h3>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-gray-700 leading-relaxed">{candidate.summary}</p>
                        </div>
                      </section>
                    )}

                    {/* Skills */}
                    {candidate.skills && candidate.skills.length > 0 && (
                      <section>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Skills</h3>
                        <div className="flex flex-wrap gap-2">
                          {candidate.skills.map((skill: string, index: number) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </section>
                    )}

                    {/* Work Experience */}
                    {candidate.experience && candidate.experience.length > 0 && (
                      <section>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Work Experience</h3>
                        <div className="space-y-4">
                          {candidate.experience.map((exp: any, index: number) => (
                            <div key={index} className="border-l-4 border-purple-500 pl-4 py-2">
                              <h4 className="font-semibold text-gray-900">{exp.title}</h4>
                              <p className="text-purple-600 font-medium">{exp.company}</p>
                              <p className="text-sm text-gray-500">{exp.duration}</p>
                              {exp.description && (
                                <p className="text-gray-700 mt-2">{exp.description}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </section>
                    )}

                    {/* Education */}
                    {candidate.education && candidate.education.length > 0 && (
                      <section>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Education</h3>
                        <div className="space-y-3">
                          {candidate.education.map((edu: any, index: number) => (
                            <div key={index} className="bg-gray-50 rounded-lg p-4">
                              <h4 className="font-semibold text-gray-900">{edu.degree}</h4>
                              <p className="text-purple-600">{edu.institution}</p>
                              <p className="text-sm text-gray-500">{edu.year}</p>
                            </div>
                          ))}
                        </div>
                      </section>
                    )}

                    {/* Cover Letter */}
                    {application.cover_letter && (
                      <section>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Cover Letter</h3>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                            {application.cover_letter}
                          </p>
                        </div>
                      </section>
                    )}

                    {/* AI Insights */}
                    {application.ai_highlights && application.ai_highlights.length > 0 && (
                      <section>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">✨ AI Highlights</h3>
                        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 space-y-2">
                          {application.ai_highlights.map((highlight: string, index: number) => (
                            <div key={index} className="flex items-start gap-2">
                              <span className="text-green-500 mt-0.5">✓</span>
                              <p className="text-gray-700">{highlight}</p>
                            </div>
                          ))}
                        </div>
                      </section>
                    )}
                  </div>
                )}

                {/* Resume Tab */}
                {activeTab === 'resume' && (
                  <div className="space-y-4">
                    {application.resume_url ? (
                      <>
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className="text-3xl">📄</span>
                            <div>
                              <p className="font-medium text-gray-900">Resume.pdf</p>
                              <p className="text-sm text-gray-500">Uploaded {new Date(application.applied_at!).toLocaleDateString()}</p>
                            </div>
                          </div>
                          <a
                            href={application.resume_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                          >
                            Download
                          </a>
                        </div>
                        <div className="border border-gray-200 rounded-lg h-[600px]">
                          <iframe
                            src={`${application.resume_url}#toolbar=0`}
                            className="w-full h-full rounded-lg"
                            title="Resume Preview"
                          />
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-12">
                        <span className="text-6xl mb-4 block">📄</span>
                        <p className="text-gray-500">No resume uploaded</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Notes Tab */}
                {activeTab === 'notes' && (
                  <div className="space-y-4">
                    <div className="space-y-3">
                      <textarea
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        placeholder="Add a note about this candidate..."
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                      <button
                        onClick={handleAddNote}
                        disabled={!newNote.trim() || savingNote}
                        className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {savingNote ? 'Saving...' : 'Add Note'}
                      </button>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      {loadingNotes ? (
                        <div className="flex justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                        </div>
                      ) : notes.length === 0 ? (
                        <p className="text-center text-gray-500 py-8">
                          No notes yet. Add your first note above! 📝
                        </p>
                      ) : (
                        <div className="space-y-4">
                          {notes.map((note) => (
                            <div key={note.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1">
                                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.note}</p>
                                  <p className="text-xs text-gray-500 mt-2">
                                    {new Date(note.created_at!).toLocaleString()}
                                  </p>
                                </div>
                                <button
                                  onClick={() => handleDeleteNote(note.id!)}
                                  className="text-red-500 hover:text-red-700 transition-colors"
                                  title="Delete note"
                                >
                                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Activity Tab */}
                {activeTab === 'activity' && (
                  <div className="space-y-4">
                    {loadingActivities ? (
                      <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                      </div>
                    ) : activities.length === 0 ? (
                      <div className="text-center py-12">
                        <span className="text-6xl mb-4 block">📊</span>
                        <p className="text-gray-500">No activity yet</p>
                        <p className="text-sm text-gray-400 mt-2">
                          Activity will appear as you interact with this application
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {activities.map((activity, index) => (
                          <div key={activity.id || index} className="flex gap-3 py-3 border-b border-gray-100 last:border-0">
                            <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-medium">
                              {activity.action_type === 'stage_change' && '🔄'}
                              {activity.action_type === 'rating_change' && '⭐'}
                              {activity.action_type === 'note_added' && '📝'}
                              {activity.action_type === 'interview_scheduled' && '📅'}
                              {activity.action_type === 'email_sent' && '✉️'}
                              {activity.action_type === 'offer_sent' && '📄'}
                              {activity.action_type === 'rejected' && '❌'}
                              {activity.action_type === 'converted_to_staff' && '✅'}
                              {!['stage_change', 'rating_change', 'note_added', 'interview_scheduled', 'email_sent', 'offer_sent', 'rejected', 'converted_to_staff'].includes(activity.action_type) && '•'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-900">{activity.description}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(activity.created_at!).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Actions & Status */}
          <div className="space-y-6">
            {/* Stage Management */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Stage</h3>
              <div className="space-y-3">
                {PIPELINE_STAGES.map((stage) => (
                  <button
                    key={stage.id}
                    onClick={() => handleStageChange(stage.id)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${
                      application.current_stage === stage.id
                        ? 'bg-purple-600 text-white shadow-lg'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span>{stage.icon}</span>
                      <span className="font-medium">{stage.label}</span>
                    </div>
                    {application.current_stage === stage.id && (
                      <span className="text-white">✓</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Rating */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Rating</h3>
              <div className="flex items-center justify-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => handleRating(star)}
                    className={`text-4xl transition-colors ${
                      (application.rating || 0) >= star
                        ? 'text-yellow-400'
                        : 'text-gray-300 hover:text-yellow-300'
                    }`}
                  >
                    ★
                  </button>
                ))}
              </div>
              <p className="text-center text-sm text-gray-500 mt-2">
                {application.rating ? `${application.rating} out of 5 stars` : 'Not rated yet'}
              </p>
            </div>

            {/* Application Details */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Application Details</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Applied</p>
                  <p className="font-medium text-gray-900">
                    {new Date(application.applied_at!).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                </div>
                {application.application_source && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Source</p>
                    <p className="font-medium text-gray-900">{application.application_source}</p>
                  </div>
                )}
                {application.ai_match_score && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">AI Match Score</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            application.ai_match_score >= 80 ? 'bg-green-500' :
                            application.ai_match_score >= 60 ? 'bg-yellow-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${application.ai_match_score}%` }}
                        />
                      </div>
                      <span className="font-bold text-gray-900">{application.ai_match_score}%</span>
                    </div>
                  </div>
                )}
                {application.salary_expectation && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Salary Expectation</p>
                    <p className="font-medium text-gray-900">
                      {application.salary_currency} {application.salary_expectation?.toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <button
                  onClick={() => setShowInterviewScheduler(true)}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Schedule Interview
                </button>
                <button
                  onClick={() => setShowEmailComposer(true)}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  Send Email
                </button>
                <button
                  onClick={() => setShowOfferGenerator(true)}
                  className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                >
                  Generate Offer
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Interview Scheduler Modal */}
      {showInterviewScheduler && (
        <InterviewScheduler
          applicationId={id!}
          candidateName={candidateName}
          candidateEmail={candidate.email}
          jobTitle={job.title || 'Unknown Position'}
          onClose={() => setShowInterviewScheduler(false)}
          onSuccess={() => {
            loadApplication();
          }}
        />
      )}

      {/* Email Composer Modal */}
      {showEmailComposer && (
        <EmailComposer
          candidateName={candidateName}
          candidateEmail={candidate.email}
          jobTitle={job.title || 'Unknown Position'}
          applicationId={id}
          onClose={() => setShowEmailComposer(false)}
          onSuccess={() => {
            loadApplication();
          }}
        />
      )}

      {/* Offer Generator Modal */}
      {showOfferGenerator && (
        <OfferGenerator
          applicationId={id!}
          candidateName={candidateName}
          candidateEmail={candidate.email}
          jobTitle={job.title || 'Unknown Position'}
          jobData={job}
          onClose={() => setShowOfferGenerator(false)}
          onSuccess={() => {
            loadApplication();
          }}
        />
      )}
    </div>
  );
};

export default ApplicationDetail;
